import React, { useCallback, useEffect, useState } from 'react';
import { Table, Switch, Select, Alert, Tag, Tooltip, Spin, Empty, InputNumber, Button } from 'antd';
import {
  EditOutlined, WarningOutlined, CheckCircleOutlined, FieldTimeOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import axiosInstance from '../../variables/api/axiosInstance';
import { can, HAB } from '../../variables/constants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';


/**
 * Quels documents portent une signature, et celle de quelle fonction.
 *
 * POURQUOI LA COLONNE « SIGNATURE VALIDE » N'ALERTE PAS. Les signatures sont
 * temporaires : le responsable en dépose une depuis son téléphone au moment de signer,
 * elle vaut une trentaine de minutes, puis son image est effacée. « Aucune signature
 * valide » est donc l'état de repos, pas une anomalie - la colonne l'affiche en gris
 * et la page n'en fait pas un avertissement. Le crier en rouge à longueur de journée
 * aurait appris aux utilisateurs à ne plus lire les alertes de cet écran.
 *
 * POURQUOI UNE FONCTION ET NON UNE PERSONNE. Le titulaire change ; un paramétrage
 * nominatif serait à refaire à chaque mouvement de personnel, et un départ laisserait
 * des documents signés au nom de quelqu'un qui n'est plus là. Le nom n'apparaît que
 * pour lever une ambiguïté, quand plusieurs agents portent la même fonction.
 */
/** Repli d'affichage tant que le serveur n'a pas répondu ; il a le même par défaut. */
const VALIDITE_DEFAUT = 30;
const VALIDITE_MIN = 5;
const VALIDITE_MAX = 240;
const CLE_VALIDITE = 'SIGNATURE_VALIDITE_MINUTES';

export default function ParametresSignatures() {
  const { t } = useTranslation('parametres');
  const [lignes, setLignes] = useState([]);
  const [profils, setProfils] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(null);
  /* Documents dont l'interrupteur vient d'être activé sans qu'une fonction soit
     choisie. Le serveur REFUSE d'enregistrer « signé » sans signataire - et il a
     raison : une telle ligne ferait sortir un document qu'on croit signé et qui ne
     l'est pas. Mais le champ de fonction ne s'affichait qu'une fois l'interrupteur
     enregistré : on ne pouvait ni cocher sans fonction, ni choisir la fonction sans
     avoir coché. L'activation reste donc LOCALE jusqu'au choix de la fonction. */
  const [enAttenteFonction, setEnAttenteFonction] = useState([]);

  const peutRegler = can(HAB.SIGNATURE_PARAMETRER);
  /* La durée de validité vit dans la table des paramètres, comme celle des OTP, et
     s'écrit donc avec PARAMETRE_UPDATE. Ce n'est pas SIGNATURE_PARAMETRER : le CSS
     règle quels documents sont signés sans pouvoir toucher aux durées système. Le
     champ reste visible pour lui, en lecture - savoir combien de temps dure une
     signature fait partie de ce qu'il paramètre. */
  const peutReglerDuree = can(HAB.PARAMETRE_UPDATE);

  const [validite, setValidite] = useState(VALIDITE_DEFAUT);
  const [ligneValidite, setLigneValidite] = useState(null);
  const [enregistrementDuree, setEnregistrementDuree] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      /* Les fonctions viennent du module signatures, et non de /v1/profils : le CSS
         détient SIGNATURE_PARAMETRER sans détenir PROFIL_READ, et sa liste déroulante
         restait vide, sans message. Celle-ci ne contient d'ailleurs que les fonctions
         réellement occupées - une fonction que personne n'occupe ne peut signer. */
      const [params, listeFonctions] = await Promise.all([
        axiosInstance.get('/v1/parametres/signatures-documents'),
        axiosInstance.get('/v1/parametres/signatures-documents/fonctions'),
      ]);
      setLignes(params.data?.data || []);
      setProfils(listeFonctions.data?.data || []);

      /* Lu à part et sans bloquer : cet écran sert même si la table des paramètres
         est inaccessible, et le serveur a de toute façon son propre repli. */
      try {
        const tous = (await axiosInstance.get('/v1/parametres', { skipErrorToast: true }))
          ?.data?.data || [];
        const ligne = tous.find((p) => p.cle === CLE_VALIDITE) || null;
        setLigneValidite(ligne);
        if (ligne?.valeur) setValidite(Number(ligne.valeur));
      } catch {
        // Silencieux : la valeur affichée reste le défaut, qui est celui du serveur.
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || i18n.t('parametres:signatures.messages.chargementImpossible'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const enregistrerDuree = async () => {
    if (!peutReglerDuree) return;
    setEnregistrementDuree(true);
    const corps = {
      typeParametre: 'SYSTEM',
      cle: CLE_VALIDITE,
      valeur: String(validite),
      description: "Duree de validite d'une signature deposee depuis le mobile (minutes, 5 a 240)",
    };
    try {
      const res = ligneValidite
        ? await axiosInstance.put(`/v1/parametres/${ligneValidite.parametreTrackingId}`, corps)
        : await axiosInstance.post('/v1/parametres', corps);
      // La ligne créée porte l'identifiant dont dépend le prochain enregistrement :
      // sans lui, un second clic recréerait une ligne au lieu de modifier la sienne.
      setLigneValidite(res?.data?.data || ligneValidite);
      toast.success(t('signatures.messages.dureeEnregistree'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('signatures.messages.echecEnregistrement'));
    } finally {
      setEnregistrementDuree(false);
    }
  };

  /**
   * Envoie la ligne entière, et non le seul champ modifié : le serveur décide de la
   * cohérence - décocher efface le signataire, cocher exige une fonction. Envoyer des
   * champs isolés obligerait le client à rejouer ces règles, et à s'en écarter un jour.
   */
  const enregistrer = async (ligne, modifications) => {
    const corps = {
      codeDocument: ligne.codeDocument,
      signatureRequise: ligne.signatureRequise,
      fonctionSignataire: ligne.fonctionSignataire,
      titulaireTrackingId: ligne.titulaireTrackingId,
      ...modifications,
    };
    setEnCours(ligne.codeDocument);
    try {
      const res = await axiosInstance.put('/v1/parametres/signatures-documents', corps, {
        skipErrorToast: true,
      });
      const majour = res.data?.data;
      setLignes((liste) => liste.map((l) => (l.codeDocument === majour.codeDocument ? majour : l)));
    } catch (e) {
      // Le serveur explique le refus - ambiguïté de titulaire, par exemple : on
      // remonte son message, qui est écrit pour être lu. Et on retire l'attente,
      // sinon l'interrupteur resterait allumé sur un réglage que le serveur a
      // refusé d'enregistrer.
      setEnAttenteFonction((liste) => liste.filter((c) => c !== ligne.codeDocument));
      toast.error(e?.response?.data?.message || t('signatures.messages.enregistrementRefuse'));
    } finally {
      setEnCours(null);
    }
  };

  const colonnes = [
    {
      title: t('signatures.colonnes.document'), dataIndex: 'libelleDocument', key: 'document', width: 280,
      render: (v) => <strong style={{ color: '#111827', fontSize: 13 }}>{v}</strong>,
    },
    {
      title: t('signatures.colonnes.signatureApposee'), key: 'requise', width: 150, align: 'center',
      render: (_, l) => (
        <Switch
          checked={l.signatureRequise || enAttenteFonction.includes(l.codeDocument)}
          disabled={!peutRegler || enCours === l.codeDocument}
          loading={enCours === l.codeDocument}
          onChange={(v) => {
            if (v && !l.fonctionSignataire) {
              // Rien n'est envoyé : on ouvre le choix de la fonction, et
              // l'enregistrement suivra ce choix.
              setEnAttenteFonction((liste) => [...liste, l.codeDocument]);
              return;
            }
            setEnAttenteFonction((liste) => liste.filter((c) => c !== l.codeDocument));
            enregistrer(l, { signatureRequise: v });
          }}
        />
      ),
    },
    {
      title: t('signatures.colonnes.signatureDe'), key: 'fonction', width: 220,
      render: (_, l) => {
        const enAttente = enAttenteFonction.includes(l.codeDocument);
        if (!l.signatureRequise && !enAttente) {
          return <span style={{ color: '#9ca3af', fontSize: 12.5 }}>-</span>;
        }
        return (
          <Select
            value={l.fonctionSignataire || undefined}
            placeholder={t('signatures.colonnes.choisirFonction')}
            disabled={!peutRegler}
            status={enAttente ? 'warning' : undefined}
            autoFocus={enAttente}
            style={{ width: '100%' }}
            options={profils.map((f) => ({ value: f, label: f }))}
            onChange={(v) => {
              setEnAttenteFonction((liste) => liste.filter((c) => c !== l.codeDocument));
              // La fonction choisie EMPORTE l'activation : c'est le geste que
              // l'utilisateur a commencé en actionnant l'interrupteur.
              enregistrer(l, {
                signatureRequise: true,
                fonctionSignataire: v,
                titulaireTrackingId: null,
              });
            }}
          />
        );
      },
    },
    {
      title: t('signatures.colonnes.quiSigne'), key: 'titulaire', width: 260,
      render: (_, l) => {
        if (!l.signatureRequise) return <span style={{ color: '#9ca3af', fontSize: 12.5 }}>-</span>;
        if (l.ambiguite) {
          return (
            <Tooltip title={t('signatures.colonnes.ambiguiteAide')}>
              <Tag icon={<WarningOutlined />} color="warning" style={{ borderRadius: 14, fontSize: 11.5 }}>
                {t('signatures.colonnes.aPreciser')}
              </Tag>
            </Tooltip>
          );
        }
        return l.titulaireNom
          ? <span style={{ fontSize: 12.5, color: '#374151' }}>{l.titulaireNom}</span>
          : <span style={{ color: '#9ca3af', fontSize: 12.5 }}>-</span>;
      },
    },
    {
      title: t('signatures.colonnes.signatureValide'), key: 'disponible', width: 190, align: 'center',
      render: (_, l) => {
        if (!l.signatureRequise) return <span style={{ color: '#9ca3af' }}>-</span>;
        return l.signatureDisponible ? (
          <Tooltip title={t('signatures.colonnes.enCoursAide')}>
            <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 14, fontSize: 11.5 }}>
              {t('signatures.colonnes.enCours')}
            </Tag>
          </Tooltip>
        ) : (
          /* Gris, et non rouge : c'est l'état de repos. Une signature dure une
             trentaine de minutes, et le titulaire en dépose une au moment de signer. */
          <Tooltip title={t('signatures.colonnes.enAttenteAide')}>
            <Tag icon={<ClockCircleOutlined />} style={{ borderRadius: 14, fontSize: 11.5 }}>
              {t('signatures.colonnes.enAttente')}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Layout title={t('signatures.page.titreOnglet')}>
      <PageHeader
        titre={t('signatures.page.titre')}
        icone={<EditOutlined />}
        compte={lignes.filter((l) => l.signatureRequise).length}
        unite={t('signatures.page.unite')}
        unitePluriel={t('signatures.page.unitePluriel')}
      />

      {!peutRegler && (
        <Alert
          type="warning" showIcon
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={t('signatures.page.consultationSeule')}
        />
      )}

      <Alert
        type="info" showIcon
        style={{ marginBottom: 16, borderRadius: 10 }}
        message={t('signatures.information.titre')}
        description={
          t('signatures.information.description', { validite })
        }
      />

      <div className="cb-card">
        {chargement ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <Table
            className="cb-table"
            rowKey="codeDocument"
            dataSource={lignes}
            columns={colonnes}
            pagination={false}
            size="small"
            locale={{ emptyText: <Empty description={t('signatures.page.aucunDocument')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            rowClassName={(l) => (l.signatureRequise && !l.signatureDisponible ? 'cb-row-alerte' : '')}
          />
        )}
      </div>

      <div className="cb-card" style={{ marginTop: 16, padding: 20 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: '#e8f5ed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FieldTimeOutlined style={{ color: '#1a6b3c', fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#111827' }}>{t('signatures.duree.titre')}</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, maxWidth: 620 }}>
              {t('signatures.duree.description', { min: VALIDITE_MIN, max: VALIDITE_MAX })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12.5, color: '#374151', fontWeight: 500 }}>{t('signatures.duree.validite')}</span>
            <InputNumber
              min={VALIDITE_MIN} max={VALIDITE_MAX} value={validite}
              onChange={(v) => setValidite(v ?? VALIDITE_DEFAUT)}
              disabled={!peutReglerDuree} addonAfter="min" style={{ width: 140 }}
            />
          </label>
          <Button
            type="primary" loading={enregistrementDuree} disabled={!peutReglerDuree}
            onClick={enregistrerDuree} style={{ background: '#1a6b3c' }}
          >
            {t('signatures.duree.enregistrer')}
          </Button>
          {!peutReglerDuree && (
            <span style={{ fontSize: 12.5, color: '#9ca3af' }}>
              {t('signatures.duree.habilitationRequise')}
            </span>
          )}
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 12.5, color: '#6b7280' }}>
        {t('signatures.page.journalisation')}
      </div>
      <style>{`.cb-row-alerte td { background: #fff7f7 !important; }`}</style>
    </Layout>
  );
}
